import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Truck } from 'lucide-react';
import { Heading } from '../layout';
import { ScheduleCalendar } from '../reusable';
import LogisticsDeliveryForm from '../forms/LogisticsDeliveryForm';
import LogisticsTripForm from '../forms/LogisticsTripForm';
import LogisticsLegForm from '../forms/LogisticsLegForm';
import LogisticsCompleteDeliveryForm from '../forms/LogisticsCompleteDeliveryForm';
import LogisticsTable3 from './LogisticsTable3';
import DeliveryDetailModal from './DeliveryDetailModal';
import DeliveryRescheduleReviewModal from './DeliveryRescheduleReviewModal';
import PeriodSummaryTable from './PeriodSummaryTable';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';
import productionProductRequestsAPI from '../../services/productionProductRequestsAPI';
import { useUIColors } from '../../hooks/useTheme';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  collectDates,
  getAvailableYearsFromDates,
  resolvePeriodSelection,
  isDateInSelectedPeriod,
  parseDateSafe,
  periodSelectStyle,
  periodLabelStyle,
} from './periodSummaryUtils';
import {
  getDeliveryStatusColor,
  getDeliveryStatusOptions,
  isDeliveredDeliveryStatus,
  isIncidentDeliveryStatus,
  isInTransitDeliveryStatus,
  normalizeDeliveryStatus,
} from '../../utils/deliveryStatus';
import { inferChileLocationFromText } from '../../config/chileRegions';

const EMPTY_DELIVERY_FORM = {
  trip_id: '',
  sale_id: '',
  sale_code: '',
  quotation_code: '',
  organization_name: '',
  delivery_code: '',
  delivery_type: 'Despacho',
  delivery_status: 'Programada',
  delivery_kind: 'Despacho Completo',
  weight_kg: '0',
  confirm_reposicion: false,
  scheduled_date: '',
  expected_delivery_date: '',
  promised_delivery_date: '',
  delivered_date: '',
  carrier_name: '',
  tracking_code: '',
  eta_close: '',
  dispatch_guide: '',
  delivery_address: '',
  pickup_warehouse: 'Bodega JR 1',
  receiver_name: '',
  receiver_phone: '',
  created_by: '',
  stop_order: '1',
  notes: '',
  quotation_comments: '',
  quotation_observations: '',
  starting_delivery: false,
  trip_mode: 'programado',
  trip_type: 'Completo',
  provider_code: '01',
  continue_from_id: '',
  _status_before_incident: '',
  started_at: '',
  invoice_number: '',
  package_count: '1',
  ship_all: true,
  start_items: [],
};

const EMPTY_TRIP_FORM = {
  trip_code: '',
  trip_date: '',
  provider_code: '01',
  carrier_name: 'LYB',
  cost: '0',
  trip_type: 'Completo',
  origin_location: '',
  destination_location: '',
  trip_status: 'Planificado',
  notes: '',
  handler_name: '',
  started_at: '',
  trip_lines: [],
};

const getDeliveryAnchorDate = (row) =>
  row?.scheduled_date || row?.delivered_date || row?.created_at || '';

const getTripAnchorDate = (row) => row?.trip_date || row?.created_at || '';

const formatSummaryCount = (value) => Number(value || 0).toLocaleString('es-CL');

const formatSummaryPercent = (value) => `${Number(value || 0).toFixed(0)}%`;

const DISPATCH_TIPO_OPTIONS = [
  'Despacho Completo',
  'Despacho Parcial',
  'Reposición',
  'Retiro en Bodega',
];

const toDispatchTipoLabel = (kind, type) => {
  const typeKey = String(type || '')
    .trim()
    .toLowerCase();
  if (typeKey.includes('retiro')) return 'Retiro en Bodega';
  const kindKey = String(kind || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (kindKey.includes('reposicion') || kindKey === 'reposición') return 'Reposición';
  if (kindKey.includes('parcial')) return 'Despacho Parcial';
  if (kindKey.includes('retiro')) return 'Retiro en Bodega';
  if (DISPATCH_TIPO_OPTIONS.includes(String(kind || '').trim())) return String(kind).trim();
  return 'Despacho Completo';
};

const toLocalDateTimeInput = (value = new Date()) => {
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const mapQuotedItemsToStartItems = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) => {
    const quoted = Number(item.quantity || item.quoted_qty || 0);
    return {
      quotation_item_id: Number(item.id || item.quotation_item_id || 0),
      product_sku: item.product_sku || '',
      item_name: item.item_name || item.product_name || '',
      color: item.color || '',
      unit: item.unit || '',
      unit_price: Number(item.unit_price || 0),
      line_total: Number(item.line_total || 0),
      quoted_qty: quoted,
      quantity: quoted,
      selected: true,
    };
  });

const applyTripToDeliveryForm = (prev, trip, logisticsProviders = []) => {
  if (!trip) {
    return { ...prev, trip_id: '' };
  }
  const provider = (logisticsProviders || []).find(
    (p) => String(p.code) === String(trip.provider_code || '')
  );
  // Continuación de tramo: conservar origen (= destino del tramo anterior) y destino final.
  const isContinuation = Boolean(prev.continue_from_id);
  return {
    ...prev,
    trip_id: String(trip.id),
    started_at: trip.started_at ? toLocalDateTimeInput(trip.started_at) : prev.started_at,
    provider_code: trip.provider_code || prev.provider_code || '01',
    carrier_name: trip.carrier_name || trip.provider_name || provider?.name || prev.carrier_name || '',
    pickup_warehouse: isContinuation
      ? prev.pickup_warehouse || trip.origin_location || ''
      : trip.origin_location || prev.pickup_warehouse || '',
    delivery_address: isContinuation
      ? prev.delivery_address || trip.destination_location || trip.destination_summary || ''
      : trip.destination_location || trip.destination_summary || prev.delivery_address || '',
  };
};

const isReposicionTipo = (value) => {
  const key = String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return key === 'reposicion' || key.includes('reposicion');
};

const PaintLogisticsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const logisticsLoadIdRef = useRef(0);
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [tripsData, setTripsData] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [pendingSalesData, setPendingSalesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [periodFilter, setPeriodFilter] = useState('last_12');
  const [secondaryYear, setSecondaryYear] = useState(String(new Date().getFullYear()));
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [calendarCollapsed, setCalendarCollapsed] = useState(false);
  const [summaryMetric, setSummaryMetric] = useState('count');
  const [summaryStatusVisibility, setSummaryStatusVisibility] = useState({});

  const [showDeliveryFormModal, setShowDeliveryFormModal] = useState(false);
  const [editingDeliveryId, setEditingDeliveryId] = useState(null);
  const [isContinuationDelivery, setIsContinuationDelivery] = useState(false);
  const [isSavingDelivery, setIsSavingDelivery] = useState(false);
  const [deliveryFormError, setDeliveryFormError] = useState('');
  const [deliveryFormData, setDeliveryFormData] = useState(EMPTY_DELIVERY_FORM);
  const [linkedProductRequests, setLinkedProductRequests] = useState([]);

  const [showTripFormModal, setShowTripFormModal] = useState(false);
  const [editingTripId, setEditingTripId] = useState(null);
  const [isSavingTrip, setIsSavingTrip] = useState(false);
  const [tripFormError, setTripFormError] = useState('');
  const [tripFormData, setTripFormData] = useState(EMPTY_TRIP_FORM);
  const [tripFormFromDelivery, setTripFormFromDelivery] = useState(false);
  const [logisticsProviders, setLogisticsProviders] = useState([
    { code: '00', name: 'Interno' },
    { code: '01', name: 'LYB' },
    { code: '02', name: 'Fedex' },
    { code: '03', name: 'TVP' },
  ]);
  const [usersData, setUsersData] = useState([]);
  const [legsData, setLegsData] = useState([]);
  const [listTab, setListTab] = useState('entregas');
  const [showCompleteFormModal, setShowCompleteFormModal] = useState(false);
  const [isSavingComplete, setIsSavingComplete] = useState(false);
  const [completeFormError, setCompleteFormError] = useState('');
  const [completeFormData, setCompleteFormData] = useState({
    delivery_id: '',
    delivery_code: '',
    sale_code: '',
    organization_name: '',
    delivered_date: '',
    outcome: 'conforme',
    notes: '',
  });
  const [detailDelivery, setDetailDelivery] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [detailQuotedItems, setDetailQuotedItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rescheduleReviewDelivery, setRescheduleReviewDelivery] = useState(null);
  const [showLegFormModal, setShowLegFormModal] = useState(false);
  const [editingLegId, setEditingLegId] = useState(null);
  const [isSavingLeg, setIsSavingLeg] = useState(false);
  const [legFormError, setLegFormError] = useState('');
  const [legFormData, setLegFormData] = useState({
    leg_code: '',
    delivery_code: '',
    delivery_id: '',
    trip_id: '',
    leg_status: 'Programada',
    carrier_name: '',
    tracking_code: '',
    eta_close: '',
    weight_kg: '0',
    stop_order: '1',
    origin_location: '',
    destination_location: '',
    notes: '',
  });

  const loadWorkspaceData = async () => {
    const loadId = ++logisticsLoadIdRef.current;
    setIsLoading(true);
    setError('');

    const parseJsonList = async (response, label) => {
      if (!response?.ok) {
        let detail = '';
        try {
          const errorData = await response.json();
          detail = errorData?.detail ? `: ${errorData.detail}` : '';
        } catch {
          /* ignore */
        }
        throw new Error(`${label} HTTP ${response?.status || 'error'}${detail}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    };

    const fetchList = async (url, label, { retries = 0, timeoutMs } = {}) => {
      let lastError;
      const attempts = Math.max(1, retries + 1);
      for (let attempt = 1; attempt <= attempts; attempt += 1) {
        try {
          const response = await authFetch(url, timeoutMs ? { timeoutMs } : undefined);
          return await parseJsonList(response, label);
        } catch (fetchError) {
          lastError = fetchError;
          if (attempt < attempts) {
            await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
          }
        }
      }
      throw lastError;
    };

    try {
      const results = await Promise.allSettled([
        fetchList(`${API_BASE_URL}/api/quotations/logistics/deliveries`, 'Deliveries', {
          retries: 2,
          timeoutMs: 45000,
        }),
        fetchList(`${API_BASE_URL}/api/quotations/logistics/trips`, 'Trips'),
        fetchList(`${API_BASE_URL}/api/quotations/logistics/calendar`, 'Calendar'),
        fetchList(`${API_BASE_URL}/api/quotations/logistics/pending-sales`, 'Pending sales'),
        fetchList(`${API_BASE_URL}/api/quotations/logistics/providers`, 'Providers'),
        fetchList(`${API_BASE_URL}/api/users/`, 'Users'),
        fetchList(`${API_BASE_URL}/api/quotations/logistics/legs`, 'Legs'),
      ]);

      // Ignore stale responses if a newer load started (save/delete/remount).
      if (loadId !== logisticsLoadIdRef.current) return;

      const [deliveriesResult, tripsResult, calendarResult, pendingResult, providersResult, usersResult, legsResult] =
        results;

      if (deliveriesResult.status === 'fulfilled') {
        setDeliveriesData(deliveriesResult.value);
      } else {
        console.error('Error loading deliveries:', deliveriesResult.reason);
        setDeliveriesData([]);
      }

      if (tripsResult.status === 'fulfilled') {
        setTripsData(tripsResult.value);
      } else {
        console.error('Error loading trips:', tripsResult.reason);
        setTripsData([]);
      }

      if (calendarResult.status === 'fulfilled') {
        setCalendarData(calendarResult.value);
      } else {
        console.error('Error loading calendar:', calendarResult.reason);
        setCalendarData([]);
      }

      if (pendingResult.status === 'fulfilled') {
        setPendingSalesData(pendingResult.value);
      } else {
        console.error('Error loading pending sales:', pendingResult.reason);
        setPendingSalesData([]);
      }

      if (legsResult.status === 'fulfilled') {
        setLegsData(legsResult.value);
      } else {
        console.error('Error loading legs:', legsResult.reason);
        setLegsData([]);
      }

      if (providersResult.status === 'fulfilled' && providersResult.value.length) {
        setLogisticsProviders(providersResult.value);
      }

      if (usersResult.status === 'fulfilled') {
        setUsersData(usersResult.value);
      } else {
        console.error('Error loading users:', usersResult.reason);
        setUsersData([]);
      }

      const criticalFailed =
        deliveriesResult.status === 'rejected' && tripsResult.status === 'rejected';
      if (criticalFailed) {
        setError('No se pudieron cargar los datos de despachos.');
      } else if (tripsResult.status === 'rejected') {
        setError('No se pudieron cargar los viajes. El resto de despachos sí cargó.');
      } else if (deliveriesResult.status === 'rejected') {
        setError('No se pudieron cargar las entregas. Los viajes sí cargaron.');
      } else {
        setError('');
      }
    } catch (fetchError) {
      if (loadId !== logisticsLoadIdRef.current) return;
      console.error('Error loading logistics workspace:', fetchError);
      setError('No se pudieron cargar los datos de despachos.');
    } finally {
      if (loadId === logisticsLoadIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadWorkspaceData();
  }, []);

  const deliveryDates = useMemo(
    () => collectDates(deliveriesData, getDeliveryAnchorDate),
    [deliveriesData]
  );

  const availableYears = useMemo(() => getAvailableYearsFromDates(deliveryDates), [deliveryDates]);

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues: deliveryDates,
        secondaryYear,
        secondaryMonth,
        titles: {
          monthly: 'Resumen despachos por período',
          annual: 'Resumen despachos anual',
        },
      }),
    [periodFilter, deliveryDates, secondaryYear, secondaryMonth]
  );

  const handlePeriodFilterChange = (value) => {
    setPeriodFilter(value);
    if (value === 'monthly' || value === 'annual') {
      const now = new Date();
      setSecondaryYear(String(now.getFullYear()));
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const summaryStatusOptions = useMemo(() => {
    const order = getDeliveryStatusOptions();
    const discovered = new Set();
    deliveriesData.forEach((row) => {
      const st = normalizeDeliveryStatus(row.delivery_status || '');
      if (st) discovered.add(st);
    });
    return [
      ...order.filter((key) => discovered.has(key)),
      ...[...discovered].filter((key) => !order.includes(key)).sort((a, b) => a.localeCompare(b, 'es')),
    ];
  }, [deliveriesData]);

  useEffect(() => {
    if (!summaryStatusOptions.length) return;
    setSummaryStatusVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      summaryStatusOptions.forEach((status) => {
        if (next[status] === undefined) {
          next[status] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [summaryStatusOptions]);

  const handleToggleSummaryStatus = (status) => {
    setSummaryStatusVisibility((prev) => ({
      ...prev,
      [status]: prev[status] === false,
    }));
  };

  const handleShowAllSummaryStatuses = () => {
    setSummaryStatusVisibility(
      summaryStatusOptions.reduce((acc, status) => {
        acc[status] = true;
        return acc;
      }, {})
    );
  };

  const handleHideAllSummaryStatuses = () => {
    setSummaryStatusVisibility(
      summaryStatusOptions.reduce((acc, status) => {
        acc[status] = false;
        return acc;
      }, {})
    );
  };

  const logisticsSummaryRows = useMemo(() => {
    const periods = selectedPeriod.columns || [];
    const periodStart = periods[0]?.start || selectedPeriod.range?.start || null;
    const isStatusVisible = (row) => {
      const status = normalizeDeliveryStatus(row.delivery_status || '') || 'Programada';
      return summaryStatusVisibility[status] !== false;
    };
    const scopedDeliveries = deliveriesData.filter(isStatusVisible);
    const scopedTrips = tripsData;

    const inBucketDelivery = (row, bucket) => {
      const date = parseDateSafe(getDeliveryAnchorDate(row));
      return Boolean(date && date >= bucket.start && date <= bucket.end);
    };
    const inBucketTrip = (row, bucket) => {
      const date = parseDateSafe(getTripAnchorDate(row));
      return Boolean(date && date >= bucket.start && date <= bucket.end);
    };
    const upToBucketDelivery = (row, bucket) => {
      const date = parseDateSafe(getDeliveryAnchorDate(row));
      if (!date || date > bucket.end) return false;
      if (periodStart && date < periodStart) return false;
      return true;
    };

    const statusOf = (row) => normalizeDeliveryStatus(row.delivery_status);
    const isPending = (row) => statusOf(row) === 'Programada';
    const isInTransit = (row) => isInTransitDeliveryStatus(statusOf(row));
    const isDelivered = (row) => isDeliveredDeliveryStatus(statusOf(row));
    const isIncident = (row) => isIncidentDeliveryStatus(statusOf(row));
    const isDispatch = (row) => {
      const tipo = String(row.dispatch_tipo || toDispatchTipoLabel(row.delivery_kind, row.delivery_type) || '');
      return !tipo.includes('Retiro');
    };

    const countBy = (predicate) =>
      periods.map((bucket) => scopedDeliveries.filter((r) => inBucketDelivery(r, bucket) && predicate(r)).length);
    const cumCountBy = (predicate) =>
      periods.map(
        (bucket) => scopedDeliveries.filter((r) => upToBucketDelivery(r, bucket) && predicate(r)).length
      );
    const tripCount = periods.map(
      (bucket) => scopedTrips.filter((r) => inBucketTrip(r, bucket)).length
    );

    const total = countBy(() => true);
    const delivered = countBy(isDelivered);
    const tasa = periods.map((_, i) => (total[i] ? (delivered[i] / total[i]) * 100 : 0));

    if (summaryMetric === 'type') {
      return [
        { label: 'Despachos', tone: 'client', values: countBy(isDispatch).map(formatSummaryCount) },
        {
          label: 'Retiros bodega',
          tone: 'prospect',
          values: countBy((r) => !isDispatch(r)).map(formatSummaryCount),
        },
        { label: 'Total entregas', tone: 'total', values: total.map(formatSummaryCount) },
        { label: 'Viajes', tone: 'neutral', values: tripCount.map(formatSummaryCount) },
      ];
    }

    return [
      { label: 'Entregas programadas', tone: 'neutral', values: total.map(formatSummaryCount) },
      { label: 'Pendientes / programadas', tone: 'prospect', values: countBy(isPending).map(formatSummaryCount) },
      { label: 'En ruta', tone: 'neutral', values: countBy(isInTransit).map(formatSummaryCount) },
      { label: 'Entregadas', tone: 'client', values: delivered.map(formatSummaryCount) },
      { label: 'Con incidente', tone: 'prospect', values: countBy(isIncident).map(formatSummaryCount) },
      { label: 'Tasa de entrega', tone: 'total', values: tasa.map(formatSummaryPercent) },
      { label: 'Total entregas (acum.)', tone: 'total', values: cumCountBy(() => true).map(formatSummaryCount) },
      { label: 'Viajes', tone: 'neutral', values: tripCount.map(formatSummaryCount) },
    ];
  }, [deliveriesData, tripsData, selectedPeriod, summaryMetric, summaryStatusVisibility]);

  const filteredDeliveries = useMemo(
    () => deliveriesData.filter((row) => isDateInSelectedPeriod(getDeliveryAnchorDate(row), selectedPeriod)),
    [deliveriesData, selectedPeriod]
  );

  const filteredLegs = useMemo(
    () =>
      (legsData || []).filter((row) =>
        isDateInSelectedPeriod(row.eta_close || row.trip_date || row.created_at || '', selectedPeriod)
      ),
    [legsData, selectedPeriod]
  );

  const filteredTrips = useMemo(
    () => tripsData.filter((row) => isDateInSelectedPeriod(getTripAnchorDate(row), selectedPeriod)),
    [tripsData, selectedPeriod]
  );

  const salesOptions = useMemo(() => {
    const fromDeliveries = (deliveriesData || []).map((d) => ({
      value: String(d.sale_id),
      label: `${d.sale_code || `V-${d.sale_id}`} · ${d.organization_name || 'Sin cliente'}`,
    }));
    const fromPending = (pendingSalesData || []).map((sale) => ({
      value: String(sale.sale_id),
      label: `${sale.sale_code || `V-${sale.sale_id}`} · ${sale.organization_name || 'Sin cliente'} (pendiente)`,
    }));
    const byId = new Map();
    [...fromPending, ...fromDeliveries].forEach((opt) => {
      if (opt.value && opt.value !== 'undefined') byId.set(opt.value, opt);
    });
    return Array.from(byId.values());
  }, [deliveriesData, pendingSalesData]);

  const tripSaleOptions = useMemo(() => {
    const byId = new Map();
    (pendingSalesData || []).forEach((sale) => {
      const id = String(sale.sale_id);
      if (!id || id === 'undefined') return;
      byId.set(id, {
        value: id,
        label: `${sale.sale_code || `V-${sale.sale_id}`} · ${sale.organization_name || 'Sin cliente'} (pendiente)`,
        sale_code: sale.sale_code || '',
        organization_name: sale.organization_name || '',
        dispatch_guide: sale.dispatch_guide || '',
      });
    });
    (deliveriesData || []).forEach((d) => {
      const id = String(d.sale_id || '');
      if (!id || id === 'undefined' || byId.has(id)) return;
      byId.set(id, {
        value: id,
        label: `${d.sale_code || `V-${id}`} · ${d.organization_name || 'Sin cliente'}`,
        sale_code: d.sale_code || '',
        organization_name: d.organization_name || '',
        dispatch_guide: d.dispatch_guide || '',
      });
    });
    (tripFormData.trip_lines || []).forEach((line) => {
      const id = String(line.sale_id || '');
      if (!id || id === '0' || byId.has(id)) return;
      byId.set(id, {
        value: id,
        label: `${line.sale_code || `V-${id}`} · ${line.organization_name || 'Sin cliente'}`,
        sale_code: line.sale_code || '',
        organization_name: line.organization_name || '',
        dispatch_guide: line.dispatch_guide || '',
      });
    });
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [pendingSalesData, deliveriesData, tripFormData.trip_lines]);

  const tripOptions = useMemo(() => {
    const tripTimestamp = (trip) => {
      const raw = trip?.started_at || trip?.created_at || trip?.trip_date || '';
      const parsed = Date.parse(raw);
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    return [...(tripsData || [])]
      .sort((a, b) => {
        const diff = tripTimestamp(b) - tripTimestamp(a);
        if (diff !== 0) return diff;
        return Number(b.id || 0) - Number(a.id || 0);
      })
      .map((trip) => ({
        value: String(trip.id),
        label: `${trip.trip_code || `TRP-${trip.id}`} · ${
          trip.provider_name || trip.carrier_name || 'Sin proveedor'
        } · ${trip.trip_date || 'sin fecha'}`,
      }));
  }, [tripsData]);

  const calendarEvents = useMemo(
    () =>
      (calendarData || []).map((row, idx) => {
        const saleLabel = row.sale_code || `Entrega ${idx + 1}`;
        const quoteLabel = String(row.quotation_code || '').trim();
        const inferred = inferChileLocationFromText(
          row.delivery_location_text || row.delivery_address || row.delivery_place || ''
        );
        const commune = String(row.commune || inferred.commune || '').trim();
        const region = String(row.region || inferred.region || '').trim();
        const location = [commune, region].filter(Boolean).join(', ');
        const subtitleParts = [quoteLabel, location].filter(Boolean);
        const statusParts = [row.delivery_status || 'Programada', row.delivery_type].filter(Boolean);
        const gallons = Number(row.tcoat_gallons || 0);
        return {
          id: row.id || idx,
          date: row.scheduled_date,
          title: `${saleLabel} · ${row.organization_name || 'Cliente'}`,
          subtitle: subtitleParts.join(' · '),
          status: statusParts.join(' · '),
          color: getDeliveryStatusColor(row.delivery_status),
          gallons: Number.isFinite(gallons) && gallons > 0 ? gallons : 0,
        };
      }),
    [calendarData]
  );

  const openCreateDeliveryForm = () => {
    setEditingDeliveryId(null);
    setIsContinuationDelivery(false);
    setDeliveryFormError('');
    setLinkedProductRequests([]);
    const today = new Date().toISOString().slice(0, 10);
    setDeliveryFormData({
      ...EMPTY_DELIVERY_FORM,
      scheduled_date: today,
      provider_code: '01',
      trip_type: 'Completo',
    });
    setShowDeliveryFormModal(true);
  };

  const buildDeliveryFormFromRow = (row, extras = {}) => {
    const hasTrip = Boolean(row.trip_id);
    return {
      trip_id: row.trip_id ? String(row.trip_id) : '',
      sale_id: row.sale_id ? String(row.sale_id) : '',
      sale_code: row.sale_code || '',
      quotation_code: row.quotation_code || '',
      organization_name: row.organization_name || '',
      delivery_code: row.delivery_code || '',
      delivery_type: row.delivery_type || 'Despacho',
      delivery_status: normalizeDeliveryStatus(row.delivery_status || 'Programada'),
      delivery_kind: row.dispatch_tipo || toDispatchTipoLabel(row.delivery_kind, row.delivery_type),
      weight_kg: String(row.weight_kg ?? 0),
      package_count: String(row.package_count ?? 1),
      invoice_number: row.invoice_number || '',
      confirm_reposicion: false,
      scheduled_date: String(row.scheduled_date || row.sale_delivery_date || '').slice(0, 10),
      expected_delivery_date: String(row.expected_delivery_date || '').slice(0, 10),
      promised_delivery_date: String(
        row.promised_delivery_date || row.expected_delivery_date || ''
      ).slice(0, 10),
      delivered_date: String(row.delivered_date || '').slice(0, 10),
      carrier_name: row.carrier_name || '',
      tracking_code: row.tracking_code || '',
      eta_close: String(row.estimated_delivery_date || '').slice(0, 10),
      dispatch_guide: row.dispatch_guide || '',
      delivery_address: row.delivery_address || '',
      pickup_warehouse: row.pickup_warehouse || 'Bodega JR 1',
      receiver_name:
        row.receiver_name ||
        row.sale_receiver_name ||
        row.purchase_contact_name ||
        '',
      receiver_phone: row.receiver_phone || '',
      created_by: row.created_by || '',
      stop_order: String(row.stop_order || 1),
      notes: row.notes || '',
      quotation_comments: String(row.quotation_comments || '')
        .split(';')
        .filter(Boolean)
        .join('\n'),
      quotation_observations: row.quotation_observations || '',
      starting_delivery: false,
      trip_mode:
        hasTrip || isInTransitDeliveryStatus(row.delivery_status) ? 'iniciado' : 'programado',
      trip_type: 'Completo',
      provider_code: '01',
      continue_from_id: '',
      _status_before_incident: '',
      ...extras,
    };
  };

  const loadLinkedProductRequests = async (deliveryId) => {
    if (!deliveryId) {
      setLinkedProductRequests([]);
      return;
    }
    try {
      const rows = await productionProductRequestsAPI.listByDelivery(deliveryId);
      setLinkedProductRequests(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('No se pudieron cargar solicitudes asociadas:', err);
      setLinkedProductRequests([]);
    }
  };

  const openEditDeliveryForm = (row) => {
    setEditingDeliveryId(row.id);
    setIsContinuationDelivery(false);
    setDeliveryFormError('');
    setDeliveryFormData(buildDeliveryFormFromRow(row));
    setLinkedProductRequests([]);
    setShowDeliveryFormModal(true);
    loadLinkedProductRequests(row.id);
  };

  const closeDeliveryDetail = () => {
    setDetailDelivery(null);
    setDetailItems([]);
    setDetailQuotedItems([]);
    setDetailLoading(false);
  };

  const fetchQuotedItemsForSale = async (saleId) => {
    if (!saleId) return [];
    const saleRes = await authFetch(`${API_BASE_URL}/api/quotations/sales/${saleId}/detail`);
    if (!saleRes.ok) return [];
    const detail = await saleRes.json();
    return (detail.items || detail.quotation_items || []).map((item) => ({
      quotation_item_id: Number(item.id || item.quotation_item_id || 0),
      product_sku: item.product_sku || '',
      product_name: item.item_name || item.product_name || '',
      color: item.color || '',
      unit: item.unit || '',
      quantity: Number(item.quantity || item.quoted_qty || 0),
      unit_price: Number(item.unit_price || 0),
      line_total: Number(item.line_total || 0),
      source: 'quotation',
    }));
  };

  const openDeliveryDetail = async (row) => {
    if (!row?.id) return;
    setDetailDelivery(row);
    setDetailItems([]);
    setDetailQuotedItems([]);
    setDetailLoading(true);
    try {
      const [itemsResult, quotedResult] = await Promise.allSettled([
        authFetch(`${API_BASE_URL}/api/quotations/logistics/deliveries/${row.id}/items`).then(
          async (res) => (res.ok ? await res.json() : [])
        ),
        fetchQuotedItemsForSale(row.sale_id),
      ]);
      const quoted = quotedResult.status === 'fulfilled' ? quotedResult.value : [];
      const stored = itemsResult.status === 'fulfilled' && Array.isArray(itemsResult.value)
        ? itemsResult.value
        : [];
      setDetailQuotedItems(quoted);
      // Sin ítems guardados aún, se muestran los cotizados como referencia.
      setDetailItems(stored.length ? stored : quoted);
    } catch (loadError) {
      console.error('Error loading delivery detail items:', loadError);
      setDetailItems([]);
      setDetailQuotedItems([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveDeliveryItems = async (deliveryId, items) => {
    const response = await authFetch(
      `${API_BASE_URL}/api/quotations/logistics/deliveries/${deliveryId}/items`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    const saved = await response.json().catch(() => []);
    setDetailItems(Array.isArray(saved) ? saved : []);
    // El prorrateo de los tramos cambia con el valor de los ítems.
    await loadWorkspaceData();
  };

  const openEditFromDeliveryDetail = (row) => {
    const delivery =
      (deliveriesData || []).find((d) => Number(d.id) === Number(row?.id)) || row;
    closeDeliveryDetail();
    openEditDeliveryForm(delivery);
  };

  const openEditLegFromDetail = (leg) => {
    if (!leg?.id) return;
    setEditingLegId(leg.id);
    setLegFormError('');
    setLegFormData({
      leg_code: leg.leg_code || '',
      delivery_code: leg.delivery_code || detailDelivery?.delivery_code || '',
      delivery_id: String(leg.delivery_id || detailDelivery?.id || ''),
      trip_id: leg.trip_id ? String(leg.trip_id) : '',
      leg_status: leg.leg_status || 'Programada',
      carrier_name: leg.carrier_name || '',
      tracking_code: leg.tracking_code || '',
      eta_close: String(leg.eta_close || '').slice(0, 10),
      weight_kg: String(leg.weight_kg ?? 0),
      stop_order: String(leg.stop_order || leg.sequence || 1),
      origin_location: leg.origin_location || leg.trip_origin || '',
      destination_location: leg.destination_location || leg.trip_destination || '',
      notes: leg.notes || '',
    });
    setShowLegFormModal(true);
  };

  const openEditTripFromDetail = (leg) => {
    const tripId = Number(leg?.trip_id || 0);
    if (!tripId) return;
    const trip = (tripsData || []).find((row) => Number(row.id) === tripId);
    if (!trip) {
      window.alert('No se encontró el viaje asociado a este tramo. Recarga Despachos e intenta de nuevo.');
      return;
    }
    openEditTripForm(trip, { fromDelivery: true });
  };

  const handleDeleteLegFromDetail = async (leg) => {
    if (!leg?.id) return;
    const label = leg.leg_code || `tramo #${leg.id}`;
    const confirmed = window.confirm(`¿Eliminar ${label}? Esta acción no se puede deshacer.`);
    if (!confirmed) return;
    try {
      const response = await authFetch(
        `${API_BASE_URL}/api/quotations/logistics/legs/${leg.id}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      await loadWorkspaceData();
    } catch (deleteError) {
      window.alert(deleteError.message || 'No se pudo eliminar el tramo.');
    }
  };

  const handleLegFormChange = (field, value) => {
    setLegFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'trip_id') {
        const trip = (tripsData || []).find((row) => String(row.id) === String(value));
        if (trip) {
          next.carrier_name = trip.carrier_name || trip.provider_name || prev.carrier_name || '';
          if (!String(prev.origin_location || '').trim()) {
            next.origin_location = trip.origin_location || '';
          }
          if (!String(prev.destination_location || '').trim()) {
            next.destination_location = trip.destination_location || trip.destination_summary || '';
          }
        }
      }
      return next;
    });
  };

  const handleSaveLeg = async () => {
    if (!editingLegId) return;
    setIsSavingLeg(true);
    setLegFormError('');
    try {
      const payload = {
        delivery_id: Number(legFormData.delivery_id || 0),
        trip_id: Number(legFormData.trip_id || 0),
        leg_code: legFormData.leg_code || '',
        leg_status: legFormData.leg_status || 'Programada',
        carrier_name: legFormData.carrier_name || '',
        tracking_code: legFormData.tracking_code || '',
        eta_close: legFormData.eta_close || '',
        weight_kg: Number(legFormData.weight_kg || 0),
        stop_order: Number(legFormData.stop_order || 1),
        origin_location: legFormData.origin_location || '',
        destination_location: legFormData.destination_location || '',
        notes: legFormData.notes || '',
      };
      const response = await authFetch(
        `${API_BASE_URL}/api/quotations/logistics/legs/${editingLegId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      setShowLegFormModal(false);
      setEditingLegId(null);
      await loadWorkspaceData();
    } catch (saveError) {
      setLegFormError(saveError.message || 'No se pudo guardar el tramo.');
    } finally {
      setIsSavingLeg(false);
    }
  };

  const openStartDeliveryForm = async (row) => {
    setEditingDeliveryId(row.id);
    setIsContinuationDelivery(false);
    setDeliveryFormError('');
    const provider =
      (logisticsProviders || []).find((p) => String(p.code) === String(row.provider_code || '01')) ||
      (logisticsProviders || [])[0];
    const base = buildDeliveryFormFromRow(row, {
      starting_delivery: true,
      trip_mode: 'iniciado',
      started_at: '',
      ship_all: true,
      start_items: [],
      pickup_warehouse: row.pickup_warehouse || 'Bodega JR 1',
      provider_code: provider?.code || '01',
      carrier_name: '',
    });
    const linkedTrip = row.trip_id
      ? (tripsData || []).find((trip) => Number(trip.id) === Number(row.trip_id))
      : null;
    setDeliveryFormData(linkedTrip ? applyTripToDeliveryForm(base, linkedTrip, logisticsProviders) : base);
    setShowDeliveryFormModal(true);
    if (!row.sale_id) return;
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/sales/${row.sale_id}/detail`);
      if (!response.ok) return;
      const detail = await response.json();
      const startItems = mapQuotedItemsToStartItems(detail.items || detail.quotation_items || []);
      setDeliveryFormData((prev) => ({
        ...prev,
        start_items: startItems,
        ship_all: true,
      }));
    } catch (loadError) {
      console.error('Error loading quoted items for start:', loadError);
    }
  };

  const openRescheduleReview = (row) => {
    if (!row?.id) return;
    setRescheduleReviewDelivery(row);
  };

  const acceptDeliveryReschedule = async (row) => {
    const response = await authFetch(
      `${API_BASE_URL}/api/quotations/logistics/deliveries/${row.id}/accept-reschedule`,
      { method: 'POST' }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    await loadWorkspaceData();
  };

  const rejectDeliveryReschedule = async (row) => {
    const response = await authFetch(
      `${API_BASE_URL}/api/quotations/logistics/deliveries/${row.id}/reject-reschedule`,
      { method: 'POST' }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    await loadWorkspaceData();
  };

  const openFinishDeliveryForm = (row) => {
    if (!row?.id) return;
    setCompleteFormError('');
    setCompleteFormData({
      delivery_id: String(row.id),
      delivery_code: row.delivery_code || '',
      sale_code: row.sale_code || '',
      organization_name: row.organization_name || '',
      delivered_date: new Date().toISOString().slice(0, 10),
      outcome: 'conforme',
      notes: '',
    });
    setShowCompleteFormModal(true);
  };

  const handleCompleteFormChange = (field, value) => {
    setCompleteFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveCompleteDelivery = async () => {
    const deliveryId = Number(completeFormData.delivery_id || 0);
    if (!deliveryId) {
      setCompleteFormError('No se encontró la entrega.');
      return;
    }
    if (completeFormData.outcome === 'incidente' && !String(completeFormData.notes || '').trim()) {
      setCompleteFormError('Describe el incidente para cerrar la entrega.');
      return;
    }
    setIsSavingComplete(true);
    setCompleteFormError('');
    try {
      const response = await authFetch(
        `${API_BASE_URL}/api/quotations/logistics/deliveries/${deliveryId}/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            delivered_date: completeFormData.delivered_date || new Date().toISOString().slice(0, 10),
            outcome: completeFormData.outcome || 'conforme',
            notes: String(completeFormData.notes || '').trim(),
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      setShowCompleteFormModal(false);
      await loadWorkspaceData();
    } catch (finishError) {
      setCompleteFormError(finishError.message || 'No se pudo finalizar la entrega.');
    } finally {
      setIsSavingComplete(false);
    }
  };

  const openContinueLegForm = (row) => {
    const deliveryLegs = (legsData || [])
      .filter((leg) => Number(leg.delivery_id) === Number(row.id))
      .sort(
        (a, b) =>
          Number(b.sequence || 0) - Number(a.sequence || 0) || Number(b.id || 0) - Number(a.id || 0)
      );
    const lastLeg = deliveryLegs[0];
    const lastTripId = lastLeg?.trip_id || row.trip_id;
    const lastTrip = (tripsData || []).find((trip) => Number(trip.id) === Number(lastTripId));
    // Origen del nuevo tramo = destino del tramo anterior (no el origen del viaje).
    const previousLegDestination = String(
      lastLeg?.destination_location ||
        lastLeg?.trip_destination ||
        lastTrip?.destination_location ||
        row.trip_destination ||
        ''
    ).trim();
    const finalAddress = row.sale_delivery_address || row.delivery_address || '';
    const today = new Date().toISOString().slice(0, 10);
    setEditingDeliveryId(null);
    setIsContinuationDelivery(true);
    setDeliveryFormError('');
    setDeliveryFormData({
      ...EMPTY_DELIVERY_FORM,
      trip_id: '',
      sale_id: row.sale_id ? String(row.sale_id) : '',
      sale_code: row.sale_code || '',
      quotation_code: row.quotation_code || '',
      organization_name: row.organization_name || '',
      delivery_code: row.delivery_code || '',
      delivery_type: row.delivery_type || 'Despacho',
      delivery_status: 'En Ruta a Tiempo',
      delivery_kind: row.dispatch_tipo || toDispatchTipoLabel(row.delivery_kind, row.delivery_type),
      weight_kg: String(row.weight_kg ?? 0),
      package_count: String(row.package_count ?? 0),
      invoice_number: row.invoice_number || '',
      scheduled_date: today,
      delivered_date: '',
      carrier_name: '',
      tracking_code: '',
      eta_close: '',
      dispatch_guide: row.dispatch_guide || '',
      pickup_warehouse: previousLegDestination,
      delivery_address: finalAddress,
      receiver_name: row.receiver_name || '',
      stop_order: '1',
      notes: `Continuación de tramo desde ${row.delivery_code || row.trip_code || `entrega #${row.id}`}.`,
      quotation_comments: String(row.quotation_comments || '')
        .split(';')
        .filter(Boolean)
        .join('\n'),
      quotation_observations: row.quotation_observations || '',
      starting_delivery: false,
      trip_mode: 'iniciado',
      trip_type: 'Ultima milla',
      provider_code: '02',
      continue_from_id: String(row.id),
    });
    setShowDeliveryFormModal(true);
  };

  const handleDeliveryFormChange = (field, value) => {
    setDeliveryFormData((prev) => {
      if (field === 'sale_id') {
        const selected =
          pendingSalesData.find((sale) => String(sale.sale_id) === String(value)) ||
          deliveriesData.find((d) => String(d.sale_id) === String(value));
        return {
          ...prev,
          sale_id: value,
          sale_code: selected?.sale_code || '',
          quotation_code: selected?.quotation_code || '',
          organization_name: selected?.organization_name || '',
          delivery_address:
            selected?.delivery_address ||
            selected?.sale_delivery_address ||
            selected?.delivery_place ||
            '',
          scheduled_date: String(
            selected?.delivery_date ||
              selected?.sale_delivery_date ||
              selected?.scheduled_date ||
              prev.scheduled_date ||
              ''
          ).slice(0, 10),
          expected_delivery_date: String(
            selected?.expected_delivery_date || prev.expected_delivery_date || ''
          ).slice(0, 10),
          promised_delivery_date: String(
            selected?.promised_delivery_date ||
              selected?.expected_delivery_date ||
              prev.promised_delivery_date ||
              ''
          ).slice(0, 10),
          receiver_name:
            selected?.receiver_name ||
            selected?.sale_receiver_name ||
            selected?.purchase_contact_name ||
            '',
          receiver_phone: selected?.receiver_phone || '',
          created_by: selected?.created_by || prev.created_by || '',
          quotation_comments: selected?.quotation_comments
            ? String(selected.quotation_comments).split(';').filter(Boolean).join('\n')
            : '',
          quotation_observations: selected?.quotation_observations || '',
        };
      }
      if (field === 'provider_code') {
        const provider = (logisticsProviders || []).find((p) => String(p.code) === String(value));
        return {
          ...prev,
          provider_code: value,
          carrier_name: provider?.name || prev.carrier_name || '',
        };
      }
      if (field === 'trip_id') {
        const trip = (tripsData || []).find((row) => String(row.id) === String(value));
        return applyTripToDeliveryForm({ ...prev, trip_id: value }, trip, logisticsProviders);
      }
      if (field === 'trip_mode') {
        const mode = value === 'iniciado' ? 'iniciado' : 'programado';
        return {
          ...prev,
          trip_mode: mode,
          trip_id: mode === 'programado' ? '' : prev.trip_id,
          delivery_status: (() => {
            const current = normalizeDeliveryStatus(prev.delivery_status);
            if (mode === 'iniciado') {
              if (current === 'Programada') return 'En Ruta a Tiempo';
              if (
                isInTransitDeliveryStatus(current) ||
                isDeliveredDeliveryStatus(current) ||
                isIncidentDeliveryStatus(current)
              ) {
                return current;
              }
              return 'En Ruta a Tiempo';
            }
            if (isInTransitDeliveryStatus(current) && current === 'En Ruta a Tiempo') {
              return 'Programada';
            }
            return current === 'Programada' ? current : prev.delivery_status || 'Programada';
          })(),
        };
      }
      if (field === 'starting_delivery') {
        return {
          ...prev,
          starting_delivery: Boolean(value),
          trip_mode: value ? 'iniciado' : prev.trip_mode,
        };
      }
      if (field === 'ship_all') {
        const shipAll = Boolean(value);
        return {
          ...prev,
          ship_all: shipAll,
          start_items: (prev.start_items || []).map((item) => ({
            ...item,
            selected: shipAll ? true : item.selected,
            quantity: shipAll ? Number(item.quoted_qty || 0) : item.quantity,
          })),
        };
      }
      if (field === 'delivery_kind') {
        return {
          ...prev,
          delivery_kind: value,
          confirm_reposicion: isReposicionTipo(value) ? prev.confirm_reposicion : false,
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSaveDelivery = async () => {
    setIsSavingDelivery(true);
    setDeliveryFormError('');
    try {
      const deliveryKind = deliveryFormData.delivery_kind || 'Despacho Completo';
      let confirmReposicion = Boolean(deliveryFormData.confirm_reposicion);
      if (
        !editingDeliveryId &&
        !isContinuationDelivery &&
        isReposicionTipo(deliveryKind) &&
        !confirmReposicion
      ) {
        throw new Error('Confirma que esta entrega es una reposición (casilla de confirmación).');
      }

      let tripId = Number(deliveryFormData.trip_id || 0);
      const tripMode = deliveryFormData.trip_mode === 'iniciado' ? 'iniciado' : 'programado';
      const startingDelivery = Boolean(deliveryFormData.starting_delivery) && Boolean(editingDeliveryId);
      const deliveryStatus =
        normalizeDeliveryStatus(
          deliveryFormData.delivery_status ||
            (tripMode === 'iniciado' ? 'En Ruta a Tiempo' : 'Programada')
        );

      const providerCode = String(deliveryFormData.provider_code || '01');
      const provider = (logisticsProviders || []).find((p) => String(p.code) === providerCode);
      const carrierName =
        String(deliveryFormData.carrier_name || '').trim() ||
        String(provider?.name || '').trim();

      if (startingDelivery) {
        if (!tripId) {
          throw new Error('Selecciona o crea un viaje para iniciar la entrega.');
        }
        const shipAll = Boolean(deliveryFormData.ship_all);
        const startItems = (deliveryFormData.start_items || [])
          .filter((item) => (shipAll ? true : Boolean(item.selected)))
          .map((item) => {
            const qty = shipAll
              ? Number(item.quoted_qty || item.quantity || 0)
              : Number(item.quantity || 0);
            const unitPrice = Number(item.unit_price || 0);
            return {
              quotation_item_id: Number(item.quotation_item_id || 0),
              product_sku: item.product_sku || '',
              product_name: item.item_name || '',
              quantity: qty,
              unit_price: unitPrice,
              line_total: Math.round(qty * unitPrice * 100) / 100,
            };
          })
          .filter((item) => item.quantity > 0);
        if ((deliveryFormData.start_items || []).length && !startItems.length) {
          throw new Error('Selecciona al menos un ítem a enviar.');
        }
        const startRes = await authFetch(
          `${API_BASE_URL}/api/quotations/logistics/deliveries/${editingDeliveryId}/start`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              trip_id: tripId,
              eta_close: deliveryFormData.eta_close || '',
              tracking_code: deliveryFormData.tracking_code || '',
              carrier_name: carrierName,
              provider_code: providerCode,
              dispatch_guide: deliveryFormData.dispatch_guide || '',
              started_at: deliveryFormData.started_at || '',
              invoice_number: deliveryFormData.invoice_number || '',
              weight_kg: Number(deliveryFormData.weight_kg || 0),
              package_count: Number(deliveryFormData.package_count || 0),
              pickup_warehouse: deliveryFormData.pickup_warehouse || '',
              delivery_address: deliveryFormData.delivery_address || '',
              ship_all: shipAll,
              items: startItems.length ? startItems : null,
            }),
          }
        );
        if (!startRes.ok) {
          const errorData = await startRes.json().catch(() => ({}));
          throw new Error(errorData.detail || `Inicio HTTP ${startRes.status}`);
        }
        setShowDeliveryFormModal(false);
        setEditingDeliveryId(null);
        setIsContinuationDelivery(false);
        setDeliveryFormData({ ...EMPTY_DELIVERY_FORM });
        await loadWorkspaceData();
        return;
      }

      // Continuación de cadena = nuevo tramo de la misma entrega
      if (!editingDeliveryId && isContinuationDelivery && deliveryFormData.continue_from_id) {
        if (!tripId) {
          throw new Error('Indica o crea un viaje para el nuevo tramo.');
        }
        const legPayload = {
          delivery_id: Number(deliveryFormData.continue_from_id),
          trip_id: tripId || 0,
          leg_status: isDeliveredDeliveryStatus(deliveryStatus) ? 'Completado' : 'Programada',
          carrier_name: carrierName,
          tracking_code: deliveryFormData.tracking_code || '',
          eta_close: deliveryFormData.eta_close || deliveryFormData.scheduled_date || '',
          weight_kg: Number(deliveryFormData.weight_kg || 0),
          stop_order: Number(deliveryFormData.stop_order || 1),
          origin_location: deliveryFormData.pickup_warehouse || '',
          destination_location: deliveryFormData.delivery_address || '',
          notes: deliveryFormData.notes || '',
          dispatch_guide: deliveryFormData.dispatch_guide || '',
          invoice_number: deliveryFormData.invoice_number || '',
          package_count: Number(deliveryFormData.package_count || 0),
        };
        const legRes = await authFetch(`${API_BASE_URL}/api/quotations/logistics/legs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(legPayload),
        });
        if (!legRes.ok) {
          const errorData = await legRes.json().catch(() => ({}));
          throw new Error(errorData.detail || `Tramo HTTP ${legRes.status}`);
        }
        setShowDeliveryFormModal(false);
        setEditingDeliveryId(null);
        setIsContinuationDelivery(false);
        setDeliveryFormData({ ...EMPTY_DELIVERY_FORM });
        await loadWorkspaceData();
        return;
      }

      const payload = {
        trip_id: tripMode === 'programado' && !editingDeliveryId ? 0 : tripId,
        sale_id: Number(deliveryFormData.sale_id || 0),
        sale_code: deliveryFormData.sale_code || '',
        quotation_code: deliveryFormData.quotation_code || '',
        organization_name: deliveryFormData.organization_name || '',
        delivery_code: deliveryFormData.delivery_code || '',
        delivery_type: deliveryFormData.delivery_type || 'Despacho',
        delivery_status: deliveryStatus,
        delivery_kind: deliveryKind,
        weight_kg: Number(deliveryFormData.weight_kg || 0),
        confirm_reposicion: isReposicionTipo(deliveryKind) ? confirmReposicion : false,
        scheduled_date: deliveryFormData.scheduled_date || '',
        expected_delivery_date: deliveryFormData.expected_delivery_date || '',
        delivered_date: deliveryFormData.delivered_date || '',
        carrier_name: carrierName,
        tracking_code: deliveryFormData.tracking_code || '',
        eta_close: deliveryFormData.eta_close || '',
        dispatch_guide: deliveryFormData.dispatch_guide || '',
        delivery_address: deliveryFormData.delivery_address || '',
        pickup_warehouse: deliveryFormData.pickup_warehouse || 'Bodega JR 1',
        receiver_name: deliveryFormData.receiver_name || '',
        receiver_phone: deliveryFormData.receiver_phone || '',
        stop_order: Number(deliveryFormData.stop_order || 1),
        notes: deliveryFormData.notes || '',
        quotation_comments: String(deliveryFormData.quotation_comments || '')
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .join(';'),
        quotation_observations: String(deliveryFormData.quotation_observations || '').trim(),
      };
      if (!editingDeliveryId && !payload.sale_id) {
        throw new Error('Indica la venta.');
      }
      if (!editingDeliveryId && tripMode === 'iniciado' && !payload.trip_id) {
        throw new Error('Indica o crea un viaje para iniciar el tramo.');
      }
      const endpoint = editingDeliveryId
        ? `${API_BASE_URL}/api/quotations/logistics/deliveries/${editingDeliveryId}`
        : `${API_BASE_URL}/api/quotations/logistics/deliveries`;
      const method = editingDeliveryId ? 'PUT' : 'POST';
      const response = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      setShowDeliveryFormModal(false);
      setEditingDeliveryId(null);
      setIsContinuationDelivery(false);
      setDeliveryFormData({ ...EMPTY_DELIVERY_FORM });
      await loadWorkspaceData();
    } catch (saveError) {
      setDeliveryFormError(saveError.message || 'No se pudo guardar la entrega.');
    } finally {
      setIsSavingDelivery(false);
    }
  };

  const handleDeleteDeliveries = async (payload) => {
    const rows = Array.isArray(payload) ? payload : [payload];
    try {
      const requests = rows.map((row) =>
        authFetch(`${API_BASE_URL}/api/quotations/logistics/deliveries/${row.id}`, { method: 'DELETE' })
      );
      const responses = await Promise.all(requests);
      if (responses.some((response) => !response.ok)) {
        throw new Error('No se pudieron eliminar todas las entregas.');
      }
      await loadWorkspaceData();
    } catch (deleteError) {
      window.alert(deleteError.message || 'Error eliminando entregas.');
    }
  };

  // El orden lo define Ajustes › Despachos › Proveedores Logísticos (campo Orden).
  const providerOptions = useMemo(
    () =>
      (logisticsProviders || [])
        .slice()
        .sort(
          (a, b) =>
            Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
            String(a.code).localeCompare(String(b.code))
        )
        .map((p) => ({
          value: String(p.code),
          label: `${p.code} · ${p.name}`,
          name: p.name,
        })),
    [logisticsProviders]
  );

  const handlerOptions = useMemo(() => {
    const options = [
      { value: '', label: 'Seleccionar ejecutivo' },
      ...(usersData || [])
        .map((user) => {
          const label = String(user.full_name || user.user_code || '').trim();
          if (!label) return null;
          return { value: label, label };
        })
        .filter(Boolean),
    ];
    const current = String(tripFormData.handler_name || '').trim();
    if (current && !options.some((opt) => opt.value === current)) {
      options.push({ value: current, label: current });
    }
    return options;
  }, [usersData, tripFormData.handler_name]);

  const fetchNextTripCode = async ({ providerCode, tripDate, apply = true } = {}) => {
    const code = providerCode || '00';
    const date = tripDate || new Date().toISOString().slice(0, 10);
    const params = new URLSearchParams({
      provider_code: code,
      trip_date: date,
    });
    const res = await authFetch(`${API_BASE_URL}/api/quotations/logistics/trips/next-code?${params}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (apply) {
      setTripFormData((prev) => ({
        ...prev,
        trip_code: data.trip_code || prev.trip_code,
        trip_date: data.trip_date || prev.trip_date || date,
        provider_code: data.provider_code || code,
        carrier_name: String(prev.carrier_name || '').trim() || data.provider_name || '',
      }));
    }
    return data;
  };

  const openCreateTripForm = async ({ fromDelivery = false } = {}) => {
    setTripFormFromDelivery(Boolean(fromDelivery));
    setEditingTripId(null);
    setTripFormError('');
    const fromDeliveryForm = Boolean(fromDelivery);
    const startedAt = fromDeliveryForm
      ? deliveryFormData.started_at || toLocalDateTimeInput()
      : '';
    const tripDate =
      (startedAt && String(startedAt).slice(0, 10)) ||
      new Date().toISOString().slice(0, 10);
    const providerCode = fromDeliveryForm
      ? deliveryFormData.provider_code || '01'
      : '01';
    const provider = (logisticsProviders || []).find((p) => String(p.code) === String(providerCode));
    setTripFormData({
      ...EMPTY_TRIP_FORM,
      trip_date: tripDate,
      started_at: startedAt || toLocalDateTimeInput(`${tripDate}T00:00`),
      provider_code: providerCode,
      carrier_name: fromDeliveryForm
        ? deliveryFormData.carrier_name || provider?.name || 'LYB'
        : provider?.name || 'LYB',
      origin_location: fromDeliveryForm
        ? deliveryFormData.pickup_warehouse || 'Bodega JR 1'
        : '',
      destination_location: fromDeliveryForm ? deliveryFormData.delivery_address || '' : '',
      trip_lines: [],
    });
    setShowTripFormModal(true);
    try {
      await fetchNextTripCode({ providerCode, tripDate });
    } catch (err) {
      console.error('Error fetching next trip code:', err);
    }
  };

  const openQuickTripFromDelivery = () => openCreateTripForm({ fromDelivery: true });

  const openEditTripForm = (row, { fromDelivery = false } = {}) => {
    setTripFormFromDelivery(Boolean(fromDelivery));
    setEditingTripId(row.id);
    setTripFormError('');
    const tripLines = (deliveriesData || [])
      .filter((d) => Number(d.trip_id) === Number(row.id))
      .sort((a, b) => Number(a.stop_order || 0) - Number(b.stop_order || 0) || Number(a.id) - Number(b.id))
      .map((d) => ({
        delivery_id: Number(d.id) || 0,
        sale_id: d.sale_id ? String(d.sale_id) : '',
        sale_code: d.sale_code || '',
        organization_name: d.organization_name || '',
        dispatch_guide: d.dispatch_guide || '',
        shipment_scope: d.shipment_scope || 'Completo',
        tracking_code: d.tracking_code || '',
        stop_order: Number(d.stop_order || 0),
      }));
    const providerCode = row.provider_code || '00';
    const provider = (logisticsProviders || []).find((p) => String(p.code) === String(providerCode));
    setTripFormData({
      trip_code: row.trip_code || '',
      trip_date: row.trip_date || '',
      // Viajes antiguos sólo tienen trip_date: el formulario ya usa un único campo con hora.
      started_at: row.started_at
        ? toLocalDateTimeInput(row.started_at)
        : row.trip_date
          ? toLocalDateTimeInput(`${String(row.trip_date).slice(0, 10)}T00:00`)
          : '',
      provider_code: providerCode,
      carrier_name: row.carrier_name || provider?.name || row.provider_name || '',
      cost: String(row.cost ?? 0),
      trip_type: row.trip_type || 'Completo',
      origin_location: row.origin_location || '',
      destination_location: row.destination_location || row.destination_summary || '',
      trip_status: row.trip_status || 'Planificado',
      notes: row.notes || '',
      handler_name: row.handler_name || '',
      trip_lines: tripLines.length ? tripLines : [],
    });
    setShowTripFormModal(true);
  };

  const handleTripFormChange = (field, value) => {
    setTripFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'provider_code') {
        const provider = (logisticsProviders || []).find((p) => String(p.code) === String(value));
        const previous = (logisticsProviders || []).find(
          (p) => String(p.code) === String(prev.provider_code || '')
        );
        const current = String(prev.carrier_name || '').trim();
        // El proveedor sólo propone el transportista: no pisar un nombre escrito a mano.
        if (!current || current === String(previous?.name || '').trim()) {
          next.carrier_name = provider?.name || '';
        }
      }
      if (field === 'started_at') {
        const datePart = String(value || '').slice(0, 10);
        if (datePart) next.trip_date = datePart;
      }
      return next;
    });
    if (!editingTripId && (field === 'provider_code' || field === 'trip_date' || field === 'started_at')) {
      const providerCode = field === 'provider_code' ? value : tripFormData.provider_code || '00';
      const tripDate =
        field === 'trip_date'
          ? value
          : field === 'started_at'
            ? String(value || '').slice(0, 10)
            : tripFormData.trip_date;
      fetchNextTripCode({ providerCode, tripDate }).catch((err) => {
        console.error('Error refreshing trip code:', err);
      });
    }
  };

  const handleSaveTrip = async () => {
    setIsSavingTrip(true);
    setTripFormError('');
    const tripLines = (Array.isArray(tripFormData.trip_lines) ? tripFormData.trip_lines : [])
      .map((line, index) => ({
        delivery_id: Number(line.delivery_id || 0),
        sale_id: Number(line.sale_id || 0),
        dispatch_guide: line.dispatch_guide || '',
        shipment_scope: line.shipment_scope || 'Completo',
        tracking_code: line.tracking_code || '',
        stop_order: Number(line.stop_order || index + 1),
      }))
      .filter((line) => line.sale_id > 0);
    const providerCode = tripFormData.provider_code || '00';
    const provider = (logisticsProviders || []).find((p) => String(p.code) === String(providerCode));
    const payload = {
      trip_code: tripFormData.trip_code || '',
      trip_date: tripFormData.trip_date || String(tripFormData.started_at || '').slice(0, 10) || '',
      started_at: tripFormData.started_at || '',
      provider_code: providerCode,
      carrier_name: String(tripFormData.carrier_name || '').trim() || provider?.name || '',
      cost: Number(tripFormData.cost || 0),
      trip_type: tripFormData.trip_type || 'Completo',
      origin_location: tripFormData.origin_location || '',
      destination_location: tripFormData.destination_location || '',
      trip_status: tripFormData.trip_status || 'Planificado',
      notes: tripFormData.notes || '',
      handler_name: tripFormData.handler_name || '',
      trip_lines: tripLines,
    };
    const endpoint = editingTripId
      ? `${API_BASE_URL}/api/quotations/logistics/trips/${editingTripId}`
      : `${API_BASE_URL}/api/quotations/logistics/trips`;
    const method = editingTripId ? 'PUT' : 'POST';
    try {
      const response = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      const savedTrip = await response.json().catch(() => ({}));
      const fromDelivery = tripFormFromDelivery && !editingTripId;
      setShowTripFormModal(false);
      setEditingTripId(null);
      setTripFormFromDelivery(false);
      setTripFormData({ ...EMPTY_TRIP_FORM });
      await loadWorkspaceData();
      if (fromDelivery && savedTrip?.id) {
        setDeliveryFormData((prev) =>
          applyTripToDeliveryForm(prev, savedTrip, logisticsProviders)
        );
      }
    } catch (saveError) {
      setTripFormError(saveError.message || 'No se pudo guardar el viaje.');
    } finally {
      setIsSavingTrip(false);
    }
  };

  const handleDeleteTrips = async (payload) => {
    const rows = Array.isArray(payload) ? payload : [payload];
    try {
      const requests = rows.map((row) =>
        authFetch(`${API_BASE_URL}/api/quotations/logistics/trips/${row.id}`, { method: 'DELETE' })
      );
      const responses = await Promise.all(requests);
      if (responses.some((response) => !response.ok)) {
        throw new Error('No se pudieron eliminar todos los viajes.');
      }
      await loadWorkspaceData();
    } catch (deleteError) {
      window.alert(deleteError.message || 'Error eliminando viajes.');
    }
  };

  const periodTitle =
    selectedPeriod?.title ||
    (periodFilter === 'all' ? 'Resumen despachos · todos los períodos' : 'Resumen despachos por período');

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'start',
          columnGap: '16px',
          rowGap: '12px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Heading level={2} isDarkMode={isDarkMode}>
            <Truck size={22} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Despachos
          </Heading>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            gap: '10px',
            flexWrap: 'nowrap',
            marginTop: '22px',
            justifySelf: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
            <span style={periodLabelStyle(ui)}>Período</span>
            <select
              value={periodFilter}
              onChange={(event) => handlePeriodFilterChange(event.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: '180px' }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Año</span>
              <select
                value={secondaryYear}
                onChange={(event) => setSecondaryYear(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '110px' }}
              >
                <option value="all">Todas</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={String(
                    secondaryYear === 'all'
                      ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                      : secondaryYear
                  )}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Mes</span>
                <select
                  value={secondaryMonth}
                  onChange={(event) => setSecondaryMonth(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>

      <PeriodSummaryTable
        periods={selectedPeriod.columns}
        rows={logisticsSummaryRows}
        title={periodTitle}
        collapsed={summaryCollapsed}
        onToggleCollapsed={() => setSummaryCollapsed((prev) => !prev)}
        segmentOptions={summaryStatusOptions}
        segmentVisibility={summaryStatusVisibility}
        onToggleSegment={handleToggleSummaryStatus}
        onShowAllSegments={handleShowAllSummaryStatuses}
        onHideAllSegments={handleHideAllSummaryStatuses}
        segmentFilterLabel="Estados"
        metricOptions={[
          { value: 'count', label: 'Por estado' },
          { value: 'type', label: 'Por tipo entrega' },
        ]}
        metric={summaryMetric}
        onMetricChange={setSummaryMetric}
      />

      <ScheduleCalendar
        events={calendarEvents}
        isDarkMode={isDarkMode}
        title="Calendario de entregas"
        allowedViews={['week', 'biweek']}
        defaultView="week"
        collapsed={calendarCollapsed}
        onToggleCollapsed={() => setCalendarCollapsed((prev) => !prev)}
      />

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Despacho</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>
              {listTab === 'tramos'
                ? `${filteredLegs.length} tramos`
                : listTab === 'viajes'
                  ? `${filteredTrips.length} viajes`
                  : `${filteredDeliveries.length} entregas`}
            </p>
          </div>
          {listTab === 'entregas' ? (
            <button
              type="button"
              onClick={openCreateDeliveryForm}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 36,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <Plus size={16} /> Nueva entrega
            </button>
          ) : null}
          {listTab === 'viajes' ? (
            <button
              type="button"
              onClick={openCreateTripForm}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 36,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <Plus size={16} /> Nuevo viaje
            </button>
          ) : null}
        </div>

        <div
          role="tablist"
          aria-label="Vistas de despacho"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            overflowX: 'auto',
          }}
        >
          {[
            { key: 'entregas', label: 'Entregas', count: filteredDeliveries.length },
            { key: 'tramos', label: 'Tramos', count: filteredLegs.length },
            { key: 'viajes', label: 'Viajes', count: filteredTrips.length },
          ].map((item) => {
            const active = listTab === item.key;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setListTab(item.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? ui.text : ui.muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{item.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : ui.muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>

        {listTab === 'entregas' ? (
          <LogisticsTable3
            variant="deliveries"
            rows={filteredDeliveries}
            loading={isLoading}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            onRowClick={openDeliveryDetail}
            onStartDelivery={openStartDeliveryForm}
            onContinueLeg={openContinueLegForm}
            onFinishDelivery={openFinishDeliveryForm}
            onReviewReschedule={openRescheduleReview}
            onBulkDelete={handleDeleteDeliveries}
          />
        ) : null}

        {listTab === 'tramos' ? (
          <LogisticsTable3
            variant="legs"
            rows={filteredLegs}
            loading={isLoading}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            onRowClick={(row) => {
              const delivery = deliveriesData.find((d) => Number(d.id) === Number(row.delivery_id));
              if (delivery) openDeliveryDetail(delivery);
            }}
          />
        ) : null}

        {listTab === 'viajes' ? (
          <LogisticsTable3
            variant="trips"
            rows={filteredTrips}
            loading={isLoading}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            onRowClick={openEditTripForm}
            onBulkDelete={handleDeleteTrips}
          />
        ) : null}
      </div>
      <DeliveryDetailModal
        isOpen={Boolean(detailDelivery)}
        onClose={closeDeliveryDetail}
        onEdit={openEditFromDeliveryDetail}
        onEditLeg={openEditLegFromDetail}
        onDeleteLeg={handleDeleteLegFromDetail}
        onEditTrip={openEditTripFromDetail}
        onSaveItems={handleSaveDeliveryItems}
        availableItems={detailQuotedItems}
        isDarkMode={isDarkMode}
        delivery={detailDelivery}
        legs={
          detailDelivery
            ? (legsData || []).filter(
                (leg) => Number(leg.delivery_id) === Number(detailDelivery.id)
              )
            : []
        }
        items={detailItems}
        loading={detailLoading}
      />
      <DeliveryRescheduleReviewModal
        isOpen={Boolean(rescheduleReviewDelivery)}
        onClose={() => setRescheduleReviewDelivery(null)}
        delivery={rescheduleReviewDelivery}
        isDarkMode={isDarkMode}
        onAccept={acceptDeliveryReschedule}
        onReject={rejectDeliveryReschedule}
      />
      <LogisticsLegForm
        isOpen={showLegFormModal}
        onClose={() => {
          if (!isSavingLeg) {
            setShowLegFormModal(false);
            setEditingLegId(null);
            setLegFormError('');
          }
        }}
        onSave={handleSaveLeg}
        isSaving={isSavingLeg}
        isDarkMode={isDarkMode}
        formData={legFormData}
        onChange={handleLegFormChange}
        formError={legFormError}
        tripOptions={tripOptions}
        zIndex={12000}
      />
      <LogisticsDeliveryForm
        isOpen={showDeliveryFormModal}
        onClose={() => {
          setShowDeliveryFormModal(false);
          setIsContinuationDelivery(false);
          setDeliveryFormData({ ...EMPTY_DELIVERY_FORM });
          setLinkedProductRequests([]);
        }}
        onSave={handleSaveDelivery}
        isSaving={isSavingDelivery}
        isDarkMode={isDarkMode}
        isEditing={Boolean(editingDeliveryId)}
        isContinuation={isContinuationDelivery}
        formData={deliveryFormData}
        onChange={handleDeliveryFormChange}
        formError={deliveryFormError}
        salesOptions={salesOptions}
        tripOptions={tripOptions}
        providerOptions={providerOptions}
        onAddTrip={openQuickTripFromDelivery}
        linkedProductRequests={linkedProductRequests}
        linkedLegs={
          editingDeliveryId
            ? (legsData || []).filter((leg) => Number(leg.delivery_id) === Number(editingDeliveryId))
            : []
        }
      />
      <LogisticsTripForm
        isOpen={showTripFormModal}
        onClose={() => {
          setShowTripFormModal(false);
          setTripFormFromDelivery(false);
        }}
        onSave={handleSaveTrip}
        isSaving={isSavingTrip}
        isDarkMode={isDarkMode}
        isEditing={Boolean(editingTripId)}
        formData={tripFormData}
        onChange={handleTripFormChange}
        formError={tripFormError}
        saleOptions={tripSaleOptions}
        providerOptions={providerOptions}
        handlerOptions={handlerOptions}
        zIndex={tripFormFromDelivery || showDeliveryFormModal || showLegFormModal ? 12000 : 10000}
      />
      <LogisticsCompleteDeliveryForm
        isOpen={showCompleteFormModal}
        onClose={() => {
          if (!isSavingComplete) {
            setShowCompleteFormModal(false);
            setCompleteFormError('');
          }
        }}
        onSave={handleSaveCompleteDelivery}
        isSaving={isSavingComplete}
        isDarkMode={isDarkMode}
        formData={completeFormData}
        onChange={handleCompleteFormChange}
        formError={completeFormError}
      />
    </div>
  );
};

export default PaintLogisticsWorkspace;
